import {
  React,
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import GolferApi from "../../services/golfer-api";
import { store } from "../../shared/store";
import Navigate from "../../shared/navigation/navigate";

const ChallengeContext = createContext();

export const ChallengeProvider = ({ children }) => {
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [isFollowingChecked, setIsFollowingChecked] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState([]);
  const [allScores, setAllScores] = useState([]);
  const [topScores, setTopScores] = useState([]);
  const [userProgress, setUserProgress] = useState(0);
  const [progressInfo, setProgressInfo] = useState("");
  const [totalScore, setTotalScore] = useState(0);
  const [currentUser, setCurrentUser] = useState(false);
  const [userName, setUserName] = useState(null);
  const [joinedChallenge, setJoinedChallenge] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 600);
  const [preview, setPreview] = useState(false);

  const pageRef = useRef(1);

  const handleResize = () => setIsSmallScreen(window.innerWidth < 600);

  const handleError = (err) => {
    console.log(err);
    Navigate.toPath("/error", {
      state: { error: err.message || "Error retrieving challenge" },
    });
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    updateSelectedFilters("Status", value);
  };

  const handleFollowingChange = () => {
    const newValue = !isFollowingChecked;
    setIsFollowingChecked(newValue);
    updateSelectedFilters("Following", newValue ? "Following" : "");
  };

  const updateSelectedFilters = (type, value) => {
    setSelectedFilters((prevFilters) => {
      const updatedFilters = prevFilters.filter(
        (filter) => filter.type !== type
      );
      if (value) {
        updatedFilters.push({ type, value });
      }
      return updatedFilters;
    });
  };

  const getData = useCallback(
    async (all = false) => {
      let status =
        selectedStatus === "All Golfers"
          ? "all"
          : selectedStatus === "In-Progress"
          ? "in_progress"
          : selectedStatus.toLowerCase();
      let params;
      if (all)
        params = { page: 1, per_page: perPage * pageRef.current, status };
      else params = { page, per_page: perPage, status };

      if (isFollowingChecked) params["following"] = true;

      const path = window.location.pathname;
      const pathParts = path.split("/");
      const idFromUrl = pathParts[pathParts.length - 1];
      let id = idFromUrl;

      var ghin_number = null;
      if (store.getState().authReducer.loginReducer.user)
        ghin_number =
          store.getState().authReducer.loginReducer.user.ghin_number;

      const promises = [];

      if (ghin_number) {
        promises.push(
          GolferApi.getGolferChallengeData(ghin_number)
            .then((res) => {
              if (res.data[0] && res.data[0].id == id) {
                setJoinedChallenge(true);
                setAllScores(res.data[0].scores);
                setTopScores(res.data[0].top_3_scores);
                setUserProgress(res.data[0].progress);
                if (res.data[0].total_net_score_to_par == "-") {
                  setTotalScore("");
                } else {
                  setTotalScore(res.data[0].total_net_score_to_par);
                }
              }
            })
            .catch((err) => {
              handleError(err);
            })
        );
      }

      promises.push(
        GolferApi.getChallengeData(id)
          .then((res) => {
            setChallengeInfo(res.data);
          })
          .catch((err) => {
            handleError(err);
          })
      );

      promises.push(
        GolferApi.getData(id, params)
          .then((res) => {
            if (res.data.mode) {
              setCurrentUser(res.data.current_user);
              setUserName(res.data.username);
            }

            let leaderboardData = [];
            if (all || page === 1) leaderboardData = res.data.leaderboard;
            else {
              leaderboardData = data.concat(res.data.leaderboard);

              let duplicatePositionIndex = -1;
              if (
                res.data.leaderboard[0]?.ghin_number === res.data.current_user
              )
                duplicatePositionIndex = leaderboardData.findLastIndex(
                  (e) => e.ghin_number === res.data.current_user
                );
              else
                duplicatePositionIndex = leaderboardData.findIndex(
                  (e) => e.ghin_number === res.data.current_user
                );

              // Removes duplicate position of the current user
              leaderboardData.splice(duplicatePositionIndex, 1);
            }

            setData(leaderboardData);
            setPages(parseInt(res.headers["total-pages"], 10));
          })
          .catch((err) => {
            handleError(err);
          })
      );

      Promise.all(promises).finally(() => {
        setIsLoading(false);
      });
    },
    [selectedStatus, isFollowingChecked, perPage, page, data]
  );

  const value = {
    challengeInfo,
    setChallengeInfo,
    isLoading,
    setIsLoading,
    selectedStatus,
    setSelectedStatus,
    isFollowingChecked,
    setIsFollowingChecked,
    perPage,
    setPerPage,
    page,
    setPage,
    pages,
    setPages,
    data,
    setData,
    allScores,
    setAllScores,
    topScores,
    setTopScores,
    userProgress,
    setUserProgress,
    progressInfo,
    setProgressInfo,
    totalScore,
    setTotalScore,
    currentUser,
    setCurrentUser,
    userName,
    setUserName,
    joinedChallenge,
    setJoinedChallenge,
    selectedFilters,
    setSelectedFilters,
    isSmallScreen,
    preview,
    setPreview,
    pageRef,
    handleStatusChange,
    handleFollowingChange,
    handleResize,
    getData,
  };

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  );
};

export const useChallenge = () => {
  const context = useContext(ChallengeContext);
  if (context === undefined) {
    throw new Error("useChallenge must be used within a ChallengeProvider");
  }
  return context;
};
